import type { EventInput as GQLInput } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * Alias, not a mirror. AppSchema's `EventInput` is already the shape the event modals build,
 * with every field optional, so a domain restatement would map nothing — and
 * `App/EventGroup/EventGroupInput.ts` already consumes the same generated type, so a second
 * shape here would be free to drift from it.
 */
export type EventInput = GQLInput;

/**
 * Every `EventInput` field, enumerated from the schema. A misspelling here is a compile
 * error; an omission is not, which is what `EventInput.spec.ts`'s round-trip test guards.
 */
const GQL_FIELDS = [
  'availableForDonation', 'availableForProcessingFee', 'availableForSalesTax',
  'confirmationEmailContent', 'donationButtonText', 'donationDescription', 'doorTime',
  'endTime', 'footerContent', 'holdPreference', 'id', 'mailPdfTickets', 'minimumAge',
  'name', 'orderConfirmationContent', 'sendWillCallReminders', 'startTime', 'ticketLimit',
  'venueId', 'venueRoomId',
] as const;

/**
 * Compile-time exhaustiveness guard. `take()` errors on a misspelled key but not on a
 * *missing* one, so without this a field added to the schema by a later codegen run would
 * silently stop persisting. This turns that into a build failure.
 */
type _AllGqlFieldsListed =
  [Exclude<keyof GQLInput, (typeof GQL_FIELDS)[number]>] extends [never] ? true : never;
const _allGqlFieldsListed: _AllGqlFieldsListed = true;
void _allGqlFieldsListed;

/**
 * `take()` omits undefined keys rather than emitting them, which is what keeps an unset `id`
 * from appearing on the create path — `id` is the only thing that tells this mutation update
 * from create.
 */
export const toGqlInput = (input: EventInput): GQLInput =>
  take<GQLInput, GQLInput>(input, ...GQL_FIELDS);
